import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";

import { api } from "../lib/api";
import { queryKeys } from "../lib/queryKeys";
import { dayAndTime } from "../lib/time";
import type { ChannelAlert, User } from "../types";

/**
 * The red strip across the top of every page while a channel has stopped, or
 * replies on it did not go out.
 *
 * In the frame rather than in Settings because Settings is where it already
 * was. saad@'s credential died on a Monday; the channel was red in Settings, the
 * queue badge had its ⚠, and each failed reply said so on its own ticket -- and
 * nobody knew until Thursday, because none of those is a place anybody looks
 * without already suspecting something. A strip on every screen of all four
 * products is impossible to work past without reading.
 *
 * Not dismissible, deliberately. It goes when the cause goes: a channel that
 * syncs again, a reply somebody sent again. A close button would put it back
 * where it was -- known to whoever happened to see it first. The API decides
 * who sees which channel and what counts (see `channels/channel-alerts.ts`), so
 * this only words it; an admin gets the way to Settings, everybody else is
 * told who can fix it.
 *
 * Polled once a minute. The socket's `message.updated` only reaches whoever
 * has that ticket open, and a sync stalling emits nothing at all, so polling
 * is the one signal that reaches everybody; it is also refetched the moment a
 * reply here fails or is retried (see `lib/socket.tsx`).
 */
export function ChannelAlerts({ user }: { user: User }) {
  const alerts = useQuery({
    queryKey: queryKeys.channelAlerts,
    queryFn: api.channelAlerts,
    refetchInterval: 60_000,
  });

  if (!alerts.data?.length) return null;

  return (
    <div
      role="alert"
      className="border-b border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800 md:px-4 dark:border-red-900 dark:bg-red-950/60 dark:text-red-200"
    >
      <ul className="space-y-1">
        {alerts.data.map((alert) => (
          <li key={alert.channelId} className="space-y-0.5">
            {alert.stalled ? (
              <StalledLine
                alert={alert}
                stalled={alert.stalled}
                isAdmin={user.role === "admin"}
              />
            ) : null}
            {alert.failedCount > 0 ? <FailedLine alert={alert} /> : null}
          </li>
        ))}
      </ul>
    </div>
  );
}

function StalledLine({
  alert,
  stalled,
  isAdmin,
}: {
  alert: ChannelAlert;
  stalled: NonNullable<ChannelAlert["stalled"]>;
  isAdmin: boolean;
}) {
  /* Gmail and eBay are pulled, so they "sync"; a WhatsApp line is pushed to
     us and the error is about what arrived. The consequence is the same --
     nothing is coming in -- and that is what the sentence leads with. */
  const since = stalled.since ? ` since ${dayAndTime(stalled.since)}` : "";
  const consequence =
    alert.type === "whatsapp"
      ? "Messages to it may not be reaching Pulse."
      : "Mail to it is not arriving and replies from it cannot send.";

  return (
    <p title={stalled.error}>
      <Warning />
      <strong className="font-semibold">{alert.address}</strong> has stopped
      working{since}. {consequence}{" "}
      {isAdmin ? (
        <Link to="/settings" className="font-medium underline underline-offset-2">
          Fix it in Settings
        </Link>
      ) : (
        <>Tell an admin: it needs fixing in Settings.</>
      )}
    </p>
  );
}

function FailedLine({ alert }: { alert: ChannelAlert }) {
  const more = alert.failedCount - alert.failedReplies.length;

  return (
    <p>
      <Warning />
      {alert.failedCount === 1 ? "A reply" : `${alert.failedCount} replies`}{" "}
      from <strong className="font-semibold">{alert.address}</strong>{" "}
      {alert.failedCount === 1 ? "was" : "were"} not sent:{" "}
      {alert.failedReplies.map((reply, index) => (
        <span key={reply.messageId}>
          {index > 0 ? " · " : null}
          <Link
            to={`/v/open/${reply.ticketId}`}
            title={`Failed ${dayAndTime(reply.sentAt)}`}
            className="underline underline-offset-2"
          >
            #{reply.ticketId}
            {reply.subject ? ` ${truncate(reply.subject, 40)}` : ""}
          </Link>
        </span>
      ))}
      {more > 0 ? ` and ${more} more` : null}{" "}
      <Link to="/failed" className="font-medium underline underline-offset-2">
        Send them again
      </Link>
    </p>
  );
}

function Warning() {
  return (
    <span aria-hidden className="mr-1.5">
      ⚠
    </span>
  );
}

function truncate(text: string, length: number): string {
  return text.length > length ? `${text.slice(0, length - 1)}…` : text;
}
